import * as React from 'react';
import { useHistory } from 'react-router-dom';
import {
    ContextualMenu,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    IContextualMenuProps,
    PrimaryButton,
} from '@fluentui/react';
import { defaultKunde, getMwstCode, useKunde } from 'api/kunde';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import moment from 'moment-timezone';
import { cloneDeep, isEqual } from 'lodash';
import { SavingState } from 'api/savingState';
import { useIsMounted } from 'hooks/useIsMounted';
import {
    Bestellung,
    addBestellung,
    updateBestellung,
    useBestellungList,
    recalculateBestellung,
    sendBestellung,
    copyBestellung,
    getDefaultBestellung,
    deleteBestellung,
    addBestellungFromBestellung,
    useBestellung,
} from 'api/bestellung';
import { formStyles } from 'styles/formStyles';
import { FormTextField, FormTextFieldOptions } from 'formComponents/FormTextField';
import { Timestamps } from 'formComponents/Timestamps';
import { BetreuerById } from 'formComponents/lookups/BetreuerById';
import { FormTextarea, FormTextareaOptions } from 'formComponents/FormTextarea';
import { Textbaustein } from 'formComponents/lookups/Textbaustein';
import { BestellungStatus } from 'formComponents/lookups/BestellungStatus';
import { FormDatePicker } from 'formComponents/FormDatePicker';
import { KundeSelect } from 'components/Kunde/KundeSelect';
import { createKundeUrl, createRecordUrl, formatMoney } from 'utils/formatter';
import { BestellungPosition, changeBestellungPositionMenge, useBestellungPositionList } from 'api/bestellungPosition';
import { AnsprechpartnerSelect } from 'components/Kunde/Ansprechpartner/AnsprechpartnerSelect';
import { Skontodefinition } from 'formComponents/lookups/Skontodefinition';
import { getApiUrl } from 'api/useApi';
import { SendMail } from 'components/SendMail/SendMail';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { LagerByIntNr } from 'formComponents/lookups/LagerByIntNr';
import { ConfirmMessage } from 'formComponents/ConfirmMessage/ConfirmMessage';
import { OpenLinkIcon } from 'formComponents/OpenLinkIcon/OpenLinkIcon';
import { BestellungStatusLabel, getBestellungLabel } from 'utils/labels';
import useRouterConfirmMessage from 'hooks/useRouterConfirmMessage';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { logDifference } from 'utils/helper';
import { OpenLink } from 'formComponents/OpenLink/OpenLink';
import { SmRefType } from 'api/attachment';
import { BestellungPositionen } from './BestellungPositionen';
import { BestellungPositionenSelect } from './BestellungPositionenSelect';
import { LinkedAttachmentList } from '../History/LinkedAttachmentList';
import { AddAttachments } from '../History/AddAttachments';

const styles = mergeStyleSets({
    markAsFinishedCheckbox: {
        marginTop: 8,
        fontWeight: 700,
        marginLeft: 5,
        marginBottom: 20,
    },
    linkBoxTitle: {
        fontWeight: 600,
        marginTop: 5,
    },
});

export interface BestellungEditData {
    kNr?: number;
    bNr: string;
}

interface BestellungEditProps {
    defaultKNr?: number;
    bNr: string; // 'neu' = new
    onClose: (refetch: boolean) => void;
    onReopen: (aNr: string) => void;
}

type ButtonAction =
    | 'reopen'
    | 'print'
    | 'printOnBriefpapier'
    | 'send'
    | 'copy'
    | 'createAbrufBestellung'
    | 'delete'
    | 'finalize';

const allowedStatus = [0, 1, 8, 4];

export const BestellungEdit: React.FC<BestellungEditProps> = ({ defaultKNr, bNr, onClose, onReopen }) => {
    const history = useHistory();

    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const isMounted = useIsMounted();

    const [refetchOnClose, setRefetchOnClose] = React.useState(false);

    const [bestellung, setBestellung] = React.useState<Bestellung | null>(getDefaultBestellung(defaultKNr));
    const dbBestellung = useBestellung(
        {
            nr: bNr,
        },
        {
            enabled: bNr !== 'neu',
            onSuccess: (loadedBestellung) => {
                setBestellung(cloneDeep(loadedBestellung));
                setSavingState(SavingState.notChanged);
            },
        },
    );

    const kunde = useKunde(
        {
            nr: bestellung?.kNr,
            withAttachmentList: true,
        },
        {
            enabled: bestellung?.kNr !== undefined,
            onSuccess: (newKunde) => {
                if (bNr === 'neu' && bestellung) {
                    setBestellung({
                        ...bestellung,
                        zahlBed: newKunde.zahlZielK || bestellung.zahlBed,
                        mwstCode: getMwstCode(newKunde),
                    });
                }
            },
        },
    );

    const abrufBestellungList = useBestellungList(
        {
            abrufZu: bNr,
            withKunde: false,
        },
        { enabled: bNr !== 'neu' && bestellung?.rahmen },
    );

    const bestellungPositionList = useBestellungPositionList(
        {
            bNr,
        },
        { enabled: bNr !== 'neu' },
    );

    // const [kunde, setKunde] = React.useState<Kunde | null>(null);
    const [showCopy, setShowCopy] = React.useState(false);
    const [showSend, setShowSend] = React.useState(false);
    const [showDelete, setShowDelete] = React.useState(false);
    const [showFinalize, setShowFinalize] = React.useState(false);
    const [showCreateAbrufBestellung, setShowCreateAbrufBestellung] = React.useState(false);
    const [changeStatusValue, setChangeStatusValue] = React.useState<number | null>(null);
    // const [markAsFinished, setMarkAsFinished] = React.useState(false);
    const [transferPositionen, setTransferPositionen] = React.useState<BestellungPosition[] | null>(null);
    const [showAddAttachments, setShowAddAttachments] = React.useState(false);

    /*
    React.useEffect(() => {
        if (
            !kundeList.isLoading &&
            !kundeList.isFetching &&
            kundeList.data?.length === 1 &&
            kundeList.data[0].nr !== kunde?.nr
        ) {
            setKunde(kundeList.data[0]);
            if (bNr === 'neu' && bestellung) {
                setBestellung({
                    ...bestellung,
                    zahlBed: kundeList?.data[0]?.zahlZielK || bestellung.zahlBed,
                    mwstCode: getMwstCode(kundeList.data[0]),
                });
            }
        }
    }, [kunde, kundeList, bNr, bestellung]);

    React.useEffect(() => {
        if (!bestellungList.isLoading && !bestellungList.isFetching && bestellung === null) {
            if (bestellungList.data?.length === 1) {
                setBestellung(cloneDeep(bestellungList.data[0]));
                setSavingState(SavingState.notChanged);
            } else {
                setBestellung(getDefaultBestellung(defaultKNr));
                setSavingState(SavingState.notSaved);
            }
        }
    }, [bestellung, bestellungList, defaultKNr, auth, kundeList]);

    React.useEffect(() => {
        if (
            savingState === SavingState.notChanged &&
            !bestellungList.isLoading &&
            !bestellungList.isFetching &&
            bestellungList.data?.length === 1 &&
            bestellung !== null &&
            !isEqual(bestellungList.data[0], bestellung)
        ) {
            setSavingState(SavingState.notSaved);
            logDifference('bestellung', bestellung, bestellungList.data[0]);
        }
    }, [savingState, bestellungList, bestellung]);
     */

    React.useEffect(() => {
        if (
            [SavingState.notSaved, SavingState.notChanged].includes(savingState) &&
            !dbBestellung.isLoading &&
            !dbBestellung.isFetching &&
            bestellung !== null
        ) {
            if (!isEqual(dbBestellung.data, bestellung)) {
                setSavingState(SavingState.notSaved);
                logDifference('bestellung', dbBestellung, dbBestellung.data);
            } else if (savingState !== SavingState.notChanged) {
                setSavingState(SavingState.notChanged);
            }
        }
    }, [savingState, dbBestellung, bestellung]);

    const readOnly = React.useMemo(() => {
        return bestellung?.status === 4;
    }, [bestellung?.status]);

    const validate = React.useCallback((): boolean => {
        if (bestellung?.kNr === undefined) {
            setValidationError('Bitte wählen Sie einen Lieferanten aus.');
            return false;
        }
        if (validationError !== null) {
            setValidationError(null);
        }

        return true;
    }, [bestellung, validationError]);

    const handleButtonAction = React.useCallback(
        (action?: ButtonAction) => {
            if (!bestellung?.nr || !action) {
                return;
            }

            switch (action) {
                case 'reopen':
                    onReopen(bestellung.nr);
                    break;
                case 'print':
                    window.open(getApiUrl('bestellung/pdf') + '?nr=' + bestellung.nr);
                    break;
                case 'printOnBriefpapier':
                    window.open(getApiUrl('bestellung/pdf') + '?nr=' + bestellung.nr + '&briefpapier=true');
                    break;
                case 'send':
                    setShowSend(true);
                    break;
                case 'copy':
                    setShowCopy(true);
                    break;
                case 'createAbrufBestellung':
                    setTransferPositionen(
                        (bestellungPositionList?.data || []).map((bp) => ({
                            ...bp,
                            transfer: bp.offen > 0,
                            transferMenge: bp.offen,
                            transferLArt: bp.lArt,
                        })),
                    );
                    setShowCreateAbrufBestellung(true);
                    break;
                case 'delete':
                    setShowDelete(true);
                    break;
                case 'finalize':
                    setTransferPositionen(
                        (bestellungPositionList?.data || []).map((bp) => ({
                            ...bp,
                            transfer: bp.offen > 0,
                            transferMenge: bp.offen,
                        })),
                    );
                    // setMarkAsFinished(true);
                    setShowFinalize(true);
                    break;
                default:
                    break;
            }
        },
        [bestellung, onReopen, bestellungPositionList],
    );

    const save = React.useCallback(
        (onFinish?: (nr: string) => void, status?: number) => {
            if (!bestellung) {
                return;
            }

            const updatedBestellung: Bestellung = status === undefined ? bestellung : { ...bestellung, status };

            if (savingState !== SavingState.saving) {
                if (validate()) {
                    setSavingState(SavingState.saving);
                    if (bNr === 'neu') {
                        addBestellung(updatedBestellung)
                            .then((result) => {
                                setSavingState(SavingState.saved);
                                setRefetchOnClose(true);
                                if (isMounted) {
                                    onReopen(result.newNr);
                                }
                                if (onFinish) {
                                    onFinish(result.newNr);
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                                setSavingState(SavingState.error);
                            });
                    } else {
                        updateBestellung(updatedBestellung)
                            .then(() => {
                                setRefetchOnClose(true);
                                if (isMounted) {
                                    dbBestellung.refetch().then();
                                    bestellungPositionList.refetch().then();
                                    setSavingState(SavingState.notChanged);
                                }
                                if (onFinish) {
                                    onFinish(bestellung.nr);
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                                setSavingState(SavingState.error);
                            });
                    }
                }
            }
        },
        [dbBestellung, bestellungPositionList, bestellung, bNr, isMounted, onReopen, savingState, validate],
    );

    const statusButtonMenuProps: IContextualMenuProps = React.useMemo(() => {
        const items = allowedStatus.map((s) => ({
            key: s.toString(),
            text: BestellungStatusLabel[s],
            onClick: () => setChangeStatusValue(s),
        }));

        return { items };
    }, []);

    const actionButtonMenuProps: IContextualMenuProps = React.useMemo(() => {
        const items = [
            {
                key: 'print',
                text: 'Drucken mit Briefkopf',
                iconProps: { iconName: 'Print' },
                onClick: () => handleButtonAction('print'),
            },
        ];

        if (bNr !== 'neu' && bestellung?.status === 0) {
            items.push({
                key: 'delete',
                text: 'Löschen',
                iconProps: { iconName: 'Delete' },
                onClick: () => handleButtonAction('delete'),
            });
        }

        return { items };
    }, [handleButtonAction, bNr, bestellung?.status]);

    const routerConfirmMessage = useRouterConfirmMessage({
        isDirty: savingState === SavingState.notSaved,
    });

    const renderTextField = (column: keyof Bestellung, label: string, options: FormTextFieldOptions = {}) => (
        <FormTextField
            value={(bestellung?.[column] || '') as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled === undefined ? readOnly : options.disabled }}
            onChange={(newValue): void => {
                if (bestellung) {
                    setBestellung({
                        ...bestellung,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderTextarea = (column: keyof Bestellung, label: string, options: FormTextareaOptions = {}) => (
        <FormTextarea
            value={bestellung?.[column] as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (bestellung) {
                    setBestellung({
                        ...bestellung,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderCheckbox = (column: keyof Bestellung, label: string) => (
        <Checkbox
            className={formStyles.checkboxInsideForm}
            label={label}
            disabled={savingState === SavingState.saving || readOnly}
            checked={bestellung?.[column] as boolean}
            onChange={(event, checked) => {
                if (bestellung) {
                    setBestellung({
                        ...bestellung,
                        [column]: checked || false,
                    });
                }
            }}
        />
    );

    const renderMa = (column: keyof Bestellung, label: string) => (
        <BetreuerById
            value={bestellung?.[column] as number}
            disabled={savingState === SavingState.saving || readOnly}
            label={label}
            allowEmpty
            menuPlacement="top"
            onChange={(newValue): void => {
                if (bestellung) {
                    setBestellung({
                        ...bestellung,
                        [column]: newValue,
                    });
                }
            }}
        />
    );

    const renderLager = () => (
        <LagerByIntNr
            value={bestellung?.lagerIntNr || 0}
            disabled={savingState === SavingState.saving || readOnly}
            label="Lager"
            allowEmpty
            menuPlacement="bottom"
            onChange={(newValue): void => {
                if (bestellung) {
                    setBestellung({
                        ...bestellung,
                        lagerIntNr: newValue,
                    });
                }
            }}
        />
    );

    const renderTextbaustein = (
        column: keyof Bestellung,
        label: string,
        kz: number,
        allowEmpty: boolean,
        menuPlacement: 'bottom' | 'top' = 'bottom',
    ) => (
        <Textbaustein
            kz={kz}
            value={bestellung?.[column] as number}
            disabled={savingState === SavingState.saving || readOnly}
            label={label}
            allowEmpty={allowEmpty}
            menuPlacement={menuPlacement}
            onChange={(newValue): void => {
                if (bestellung) {
                    setBestellung({
                        ...bestellung,
                        [column]: parseInt(newValue, 10) || 0,
                    });
                }
            }}
        />
    );

    const renderDatePicker = (
        column: keyof Bestellung,
        label: string,
        required = false,
        defaultNow = false,
        disabled: boolean | undefined = undefined,
    ) => {
        return (
            <FormDatePicker
                label={label}
                savingState={savingState}
                value={(bestellung?.[column] as string) || (defaultNow ? moment().toISOString() : undefined)}
                onChange={(newValue) => {
                    if (bestellung) {
                        setBestellung({
                            ...bestellung,
                            [column]: newValue,
                        });
                    }
                }}
                options={{
                    required,
                    disabled: disabled === undefined ? readOnly : disabled,
                }}
            />
        );
    };

    const renderBestellungStatus = () => (
        <BestellungStatus
            value={bestellung?.status?.toString() || '10'}
            disabled={savingState === SavingState.saving || readOnly}
            onChange={(newValue): void => {
                if (bestellung) {
                    setBestellung({
                        ...bestellung,
                        status: parseInt(newValue, 10),
                    });
                }
            }}
        />
    );

    const renderKundeSelect = () => (
        <KundeSelect
            title="Lieferantenauswahl"
            label="Lieferant"
            kNr={bestellung?.kNr}
            name={kunde.data?.name1}
            disabled={savingState === SavingState.saving || readOnly || bNr !== 'neu'}
            onChange={(newKNr): void => {
                if (bestellung && bestellung.kNr !== newKNr) {
                    setBestellung({
                        ...bestellung,
                        kNr: newKNr,
                        kaNr: 0,
                        kaName: '',
                    });
                }
            }}
        />
    );

    const renderAnsprechpartner = () => (
        <AnsprechpartnerSelect
            label="Ansprechpartner"
            kNr={bestellung?.kNr}
            kaNr={bestellung?.kaNr}
            kaName={bestellung?.kaName}
            disabled={savingState === SavingState.saving || readOnly}
            allowEmpty
            onChange={(newKaNr, newKaName): void => {
                if (bestellung) {
                    setBestellung({
                        ...bestellung,
                        kaNr: newKaNr || 0,
                        kaName: newKaName || '',
                    });
                }
            }}
        />
    );

    const renderSkontodefinition = () => (
        <Skontodefinition
            value={bestellung?.zahlBed || 0}
            label="Zahlungsbedingungen"
            disabled={savingState === SavingState.saving || readOnly}
            onChange={(newValue): void => {
                if (bestellung) {
                    setBestellung({
                        ...bestellung,
                        zahlBed: newValue,
                    });
                }
            }}
        />
    );

    return (
        <>
            {routerConfirmMessage.render()}
            <Dialog
                hidden={false}
                title={getBestellungLabel(bestellung, kunde.data)}
                dialogContentProps={{
                    type: DialogType.normal,
                }}
                modalProps={{
                    isBlocking: true,
                    dragOptions: {
                        moveMenuItemText: 'Move',
                        closeMenuItemText: 'Close',
                        menu: ContextualMenu,
                    },
                }}
                minWidth="95%"
                maxWidth="95%"
                onDismiss={() => {
                    onClose(refetchOnClose);
                }}
            >
                <form className={formStyles.form}>
                    <div className={formStyles.threeColumnsBlock}>
                        <div className={formStyles.firstColumn}>
                            <div className={formStyles.gridCell}>
                                {renderKundeSelect()}
                                {kunde ? <OpenLinkIcon link={createKundeUrl(kunde.data?.nr.toString())} /> : null}
                            </div>
                            <div className={formStyles.gridCell}>{renderAnsprechpartner()}</div>

                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('nr', 'Bestell-Nr.', { maxLength: 10, disabled: true })}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('auNr', 'Auftrag-Nr.', { maxLength: 10 })}
                                    {bestellung?.auNr ? (
                                        <OpenLinkIcon
                                            link={createRecordUrl('auftraege', defaultKNr, bestellung?.auNr)}
                                        />
                                    ) : null}
                                </div>
                            </div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>
                                    {renderCheckbox('rahmen', 'Rahmenbestellung')}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('abrufZu', 'Abruf zu', { maxLength: 10, disabled: true })}
                                    {bestellung?.abrufZu ? (
                                        <OpenLinkIcon
                                            link={createRecordUrl('bestellungen', defaultKNr, bestellung?.abrufZu)}
                                            runBefore={() => {
                                                onClose(false);
                                            }}
                                        />
                                    ) : null}
                                </div>
                            </div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>
                                    {renderDatePicker('datum', 'Ausstellungsdatum', true, true)}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderDatePicker('datBis', 'Bestellung gültig bis')}
                                </div>
                                <div className={formStyles.gridCell}>{renderMa('betreuer', 'Betreuer')}</div>
                            </div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>
                                    {renderDatePicker('datumExt', 'Lieferdatum d. Lieferanten', false, false, false)}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('nrExt', 'Lieferantenrechnungsnummer', {
                                        maxLength: 250,
                                        disabled: false,
                                    })}
                                </div>
                            </div>

                            {bestellung?.rahmen && (
                                <div className={formStyles.twoColumnsBlock}>
                                    <div className={formStyles.gridCell}>
                                        <div className={styles.linkBoxTitle}>Abrufbestellung</div>
                                        {(abrufBestellungList.data || []).map((abrufBestellung) => (
                                            <div>
                                                <OpenLink
                                                    link={createRecordUrl(
                                                        'bestellungen',
                                                        defaultKNr,
                                                        abrufBestellung?.nr,
                                                    )}
                                                    label={
                                                        moment(abrufBestellung.datum).format('DD.MM.YYYY') +
                                                        ': ' +
                                                        abrufBestellung.nr
                                                    }
                                                    runBefore={() => {
                                                        onClose(false);
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    <div />
                                </div>
                            )}

                            <div className={formStyles.gridCell}>
                                {kunde.data?.attachmentList && bNr !== 'neu' && (
                                    <LinkedAttachmentList
                                        kNr={kunde.data.nr}
                                        smRefType={SmRefType.bestellung}
                                        smRef={bNr}
                                        attachmentList={kunde.data.attachmentList}
                                        refetchKunde={() => kunde.refetch()}
                                        onAdd={() => setShowAddAttachments(true)}
                                    />
                                )}
                            </div>
                        </div>

                        <div className={formStyles.secondColumn}>
                            <div className={formStyles.gridCell}>{renderLager()}</div>
                            <div className={formStyles.gridCell}>{renderTextarea('txt', 'Text')}</div>
                            <div className={formStyles.gridCell}>
                                {renderTextbaustein('txtB1', 'Textbaustein 1', 23, true)}
                            </div>
                            <div className={formStyles.gridCell}>
                                {renderTextbaustein('txtB2', 'Textbaustein 2', 23, true, 'top')}
                            </div>
                            <div className={formStyles.gridCell}>
                                {renderTextbaustein('txtB3', 'Textbaustein 3', 23, true, 'top')}
                            </div>
                            <div className={formStyles.gridCell}>
                                {renderTextbaustein('txtB4', 'Textbaustein 4', 23, true, 'top')}
                            </div>
                        </div>

                        <div className={formStyles.thirdColumn}>
                            <div className={formStyles.gridCell}>{renderBestellungStatus()}</div>
                            <div className={formStyles.gridCell}>
                                {renderTextbaustein('liefBed', 'Lieferbedingung', 11, true)}
                            </div>
                            <div className={formStyles.gridCell}>
                                {renderTextbaustein('liefZeit', 'Lieferzeit', 14, true)}
                            </div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>{renderDatePicker('liefDat', 'Lieferdatum')}</div>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('liefKw', 'Liefer-KW', { numeric: true })}
                                </div>
                            </div>
                            <div className={formStyles.gridCell}>{renderSkontodefinition()}</div>
                            <div className={formStyles.gridCell}>
                                {renderTextField('aktText', 'Betreff E-Akt', { maxLength: 100 })}
                            </div>
                        </div>
                    </div>

                    <div className={formStyles.threeColumnsBlock}>
                        <div className={formStyles.firstColumn}>
                            <div className={formStyles.gridCell}>
                                <FormTextarea
                                    value={kunde.data?.bemerkung || ''}
                                    label="Bemerkung (beim Lieferanten)"
                                    onChange={() => {}}
                                    savingState={savingState}
                                    options={{ disabled: true }}
                                />
                            </div>
                        </div>
                        <div className={formStyles.secondColumn}>
                            <FormTextarea
                                value={kunde.data?.liefBem || ''}
                                label="Lieferbemerkung (beim Lieferanten)"
                                onChange={() => {}}
                                savingState={savingState}
                                options={{ disabled: true }}
                            />
                        </div>
                    </div>

                    <div>
                        {bestellung && bestellungPositionList?.data && (
                            <BestellungPositionen
                                kunde={kunde.data || defaultKunde}
                                bestellung={bestellung}
                                bestellungPositionen={bestellungPositionList?.data}
                                viewMode="long"
                                allowEdit={!readOnly && savingState === SavingState.notChanged}
                                refetch={async () => {
                                    const result = await recalculateBestellung(bestellung);
                                    if (result?.bestellung) {
                                        dbBestellung.refetch().then();
                                        setBestellung({
                                            ...bestellung,
                                            sNetto: result.bestellung.sNetto,
                                            sBrutto: result.bestellung.sBrutto,
                                            sMwst: result.bestellung.sMwst,
                                        });
                                        bestellungPositionList.refetch().then();
                                    }

                                    setRefetchOnClose(true);
                                }}
                            />
                        )}
                    </div>

                    {bestellungPositionList?.data && (
                        <div className={formStyles.threeColumnsBlock}>
                            <div className={formStyles.firstColumn}>
                                <div className={formStyles.sumLine}>
                                    Summe Netto: {formatMoney(bestellung?.sNetto || 0)}
                                </div>
                            </div>
                            <div className={formStyles.secondColumn}>
                                <div className={formStyles.sumLine}>
                                    Summe Brutto: {formatMoney(bestellung?.sBrutto || 0)}
                                </div>
                            </div>
                            <div className={formStyles.thirdColumn}>
                                <div className={formStyles.sumLine}>
                                    Summe MwSt.: {formatMoney(bestellung?.sMwst || 0)}
                                </div>
                            </div>
                        </div>
                    )}

                    <Timestamps
                        createdDate={bestellung?.anlageDat}
                        createdDecimalTime={bestellung?.anlageUhr}
                        createdUser={bestellung?.anlageUser}
                        modifiedDate={bestellung?.updDat}
                        modifiedDecimalTime={bestellung?.updUhr}
                        modifiedUser={bestellung?.updUser}
                    />

                    {validationError && <div className={formStyles.error}>{validationError}</div>}
                    {savingState === SavingState.error && (
                        <div className={formStyles.error}>Fehler beim Speichern.</div>
                    )}

                    <DialogFooter>
                        <DefaultButton
                            className={formStyles.copyButton}
                            onClick={() => handleButtonAction('copy')}
                            text="Kopieren"
                            disabled={savingState !== SavingState.notChanged || !bestellung?.nr}
                        />
                        <DefaultButton
                            className={formStyles.sendButton}
                            onClick={() => handleButtonAction('send')}
                            text="Senden"
                            disabled={savingState !== SavingState.notChanged || !bestellung?.nr}
                        />
                        {bestellung?.rahmen && (
                            <DefaultButton
                                className={formStyles.migrateButton}
                                onClick={() => handleButtonAction('createAbrufBestellung')}
                                text="Abrufbestellung erstellen"
                                disabled={
                                    savingState !== SavingState.notChanged ||
                                    !bestellung?.nr ||
                                    bestellung?.status === 4
                                }
                            />
                        )}
                        {bestellung?.rahmen || (
                            <DefaultButton
                                className={formStyles.finalizeButton}
                                onClick={() => handleButtonAction('finalize')}
                                text="Lieferung einbuchen"
                                disabled={
                                    savingState !== SavingState.notChanged ||
                                    !bestellung?.nr ||
                                    bestellung?.status === 4
                                }
                            />
                        )}
                        <DefaultButton
                            split
                            splitButtonAriaLabel="Status"
                            aria-roledescription="action button"
                            disabled={savingState !== SavingState.notChanged || !bestellung?.nr}
                            text="Status ändern"
                            menuProps={statusButtonMenuProps}
                            onClick={() => {}}
                        />
                        <DefaultButton
                            split
                            splitButtonAriaLabel="Weitere Aktionen"
                            aria-roledescription="action button"
                            disabled={savingState !== SavingState.notChanged || !bestellung?.nr}
                            onClick={() => handleButtonAction('printOnBriefpapier')}
                            text="Drucken"
                            menuProps={actionButtonMenuProps}
                        />
                        {savingState === SavingState.saving ? (
                            <PrimaryButton text="Speichere..." disabled />
                        ) : (
                            <PrimaryButton
                                onClick={() => save()}
                                text="Speichern"
                                disabled={savingState === SavingState.notChanged || dbBestellung.isLoading}
                            />
                        )}
                    </DialogFooter>
                </form>
                {(dbBestellung.isLoading ||
                    dbBestellung.isFetching ||
                    kunde.isLoading ||
                    kunde.isFetching ||
                    !bestellung) && <LoadingIndicator />}
            </Dialog>
            {showCopy && (
                <KundeSelect
                    label="Kunde"
                    disabled={savingState === SavingState.saving || readOnly || bNr !== 'neu'}
                    dialogOnly
                    initFilterKNr={bestellung?.kNr}
                    onChange={async (newKNr) => {
                        if (bestellung && newKNr) {
                            const { newNr } = await copyBestellung(newKNr, bestellung, bestellungPositionList?.data);
                            setShowCopy(false);
                            onReopen(newNr);
                        }
                    }}
                    onCancel={() => setShowCopy(false)}
                />
            )}
            {showSend && (
                <SendMail
                    kNr={bestellung?.kNr || 0}
                    kaNr={bestellung?.kaNr}
                    markAsSentLabel="Bestellung als versendet markieren"
                    onSubmit={(kaNrListTo, kaNrListCC, markAsSent) => {
                        if (bestellung) {
                            sendBestellung(bestellung, kaNrListTo, kaNrListCC).then(() => {
                                setShowSend(false);
                                if (markAsSent && bestellung) {
                                    save(undefined, 1);
                                }
                            });
                        }
                    }}
                    onCancel={() => setShowSend(false)}
                />
            )}
            {showDelete && (
                <ConfirmMessage
                    title="Bestellung löschen"
                    message={'Soll die Bestellung ' + bestellung?.nr + ' gelöscht werden?'}
                    onConfirm={() => {
                        if (bestellung?.nr) {
                            deleteBestellung(bestellung).then(() => {
                                onClose(true);
                            });
                        }
                    }}
                    onCancel={() => setShowDelete(false)}
                />
            )}
            {showCreateAbrufBestellung && (
                <ConfirmMessage
                    title="Abrufbestellung erstellen"
                    message={'Aus der Rahmenbestellung ' + bestellung?.nr + ' wird ein Abrufauftrag erstellt.'}
                    minWidth={1000}
                    confirmText="Abrufbestellung erstellen"
                    cancelText="Abbruch"
                    content={
                        <>
                            <ul>
                                <li>Eine Abrufbestellung mit den gewählten Positionen und Mengen wird erstellt.</li>
                                <li>Die vorgeschlagenen Mengen wurden aus &apos;Offen&apos; übernommen.</li>
                                <li>Die bestätigten Mengen werden von der Rahmenbestellung abgezogen.</li>
                                <li>Die neue Abrufbestellung wird geöffnet.</li>
                                <li>
                                    Wenn Lieferart <strong>Lager</strong> gewählt wird, wird{' '}
                                    <strong>Offene Bestellungen</strong> in der Lagerverwaltung erhöht.
                                </li>
                            </ul>
                            <BestellungPositionenSelect
                                bestellungPositionen={transferPositionen || []}
                                onChange={(newTransferPositionen) => setTransferPositionen(newTransferPositionen)}
                                withLArt
                            />
                        </>
                    }
                    onConfirm={async () => {
                        if (bestellung?.nr) {
                            await addBestellungFromBestellung(bestellung, transferPositionen || []).then((result) => {
                                onClose(true);
                                history.push(createRecordUrl('bestellungen', bestellung.kNr, result.newNr));
                            });
                        }
                    }}
                    onCancel={() => {
                        setShowCreateAbrufBestellung(false);
                        setTransferPositionen(null);
                    }}
                />
            )}
            {showFinalize && (
                <ConfirmMessage
                    title="Lieferung einbuchen"
                    message={'Soll eine Lieferung zu Bestellung ' + bestellung?.nr + ' eingebucht werden?'}
                    minWidth={1000}
                    cancelText="Abbruch"
                    content={
                        <>
                            <ul>
                                <li>
                                    Bei Lieferart <strong>Lager</strong>, wird <strong>Offene Bestellungen</strong> in
                                    der Lagerverwaltung reduziert und <strong>Bestand</strong> erhöht.
                                </li>
                                <li>
                                    Der Status der Bestellung wird auf <strong>Teillieferung</strong> bzw.{' '}
                                    <strong>abgeschlossen</strong> gesetzt.
                                </li>
                            </ul>
                            <BestellungPositionenSelect
                                bestellungPositionen={transferPositionen || []}
                                onChange={(newTransferPositionen) => {
                                    setTransferPositionen(newTransferPositionen);
                                }}
                                withLArt={false}
                            />
                        </>
                    }
                    onConfirm={async () => {
                        if (kunde && bestellung?.nr) {
                            let offenTotal = 0;
                            // eslint-disable-next-line no-restricted-syntax
                            for (const transferPosition of transferPositionen || []) {
                                if (transferPosition.transfer && (transferPosition.transferMenge || 0) > 0) {
                                    // eslint-disable-next-line no-await-in-loop
                                    await changeBestellungPositionMenge(
                                        {
                                            ...transferPosition,
                                            offen: transferPosition.offen - (transferPosition.transferMenge || 0),
                                        },
                                        true,
                                    );
                                    offenTotal += transferPosition.offen - (transferPosition.transferMenge || 0);
                                } else {
                                    offenTotal += transferPosition.offen;
                                }
                            }

                            if (offenTotal > 0) {
                                save(() => setShowFinalize(false), 8);
                            } else {
                                save(() => setShowFinalize(false), 4);
                                // onReopen(bestellung.nr);
                            }

                            /*
                            await finalizeBestellung(bestellung).then(() => {
                                onReopen(bestellung.nr);
                            });
                             */
                        }
                    }}
                    onCancel={() => setShowFinalize(false)}
                />
            )}

            {changeStatusValue !== null && (
                <ConfirmMessage
                    title="Status ändern"
                    message={
                        'Soll der Status von Bestellung ' +
                        bestellung?.nr +
                        ' auf "' +
                        BestellungStatusLabel[changeStatusValue] +
                        '" geändert werden?'
                    }
                    onConfirm={() => {
                        if (bestellung?.nr) {
                            save(() => setChangeStatusValue(null), changeStatusValue);
                        }
                    }}
                    onCancel={() => setChangeStatusValue(null)}
                />
            )}
            {showAddAttachments && kunde.data && bNr !== 'neu' && (
                <AddAttachments
                    kNr={kunde.data.nr}
                    forcedSmRefType={SmRefType.bestellung}
                    forcedSmRef={bNr}
                    onClose={(refetch) => {
                        setShowAddAttachments(false);
                        if (refetch) {
                            kunde.refetch().then();
                        }
                    }}
                />
            )}
        </>
    );
};
